import React, { useEffect, useState } from "react";
import type { CourseProgressSummary } from "../appTypes";
import type { HomeBanner, ProductItem } from "../services/api";
import Banner from "../components/Banner";
import ContentCard from "../components/ContentCard";
import "./HomePage.css";

interface OverviewCard {
  label: string;
  value: string;
  helper: string;
}

interface LastLesson {
  courseId: string;
  courseTitle: string;
  moduleTitle: string;
  lessonId: number;
  lessonTitle: string;
  cover: string;
}

interface LiveConfig {
  enabled: boolean;
  title: string;
  subtitle: string;
  url: string;
  days: number[];
  start: string;
  end: string;
  offlineText: string;
}

const DAY_NAMES = ["domingo", "segunda", "terça", "quarta", "quinta", "sexta", "sábado"];

function getLiveStatus(live?: LiveConfig): { show: boolean; isLive: boolean; countdown?: string } {
  if (!live?.enabled) return { show: false, isLive: false };
  const now = new Date();
  const [sh, sm] = (live.start || "20:00").split(":").map(Number);
  const [eh, em] = (live.end || "22:00").split(":").map(Number);
  const day = now.getDay();
  const mins = now.getHours() * 60 + now.getMinutes();
  const startMins = sh * 60 + sm;
  const endMins = eh * 60 + em;
  const days = live.days && live.days.length ? live.days : [1, 2, 3, 4, 5];
  if (days.includes(day) && mins >= startMins && mins < endMins) {
    return { show: true, isLive: true };
  }
  for (let i = 0; i <= 7; i++) {
    const d = (day + i) % 7;
    if (!days.includes(d)) continue;
    if (i === 0 && mins >= startMins) continue;
    const next = new Date(now);
    next.setDate(now.getDate() + i);
    next.setHours(sh, sm, 0, 0);
    const diffMs = next.getTime() - now.getTime();
    const diffH = Math.floor(diffMs / 3600000);
    const diffM = Math.floor((diffMs % 3600000) / 60000);
    let countdown: string;
    if (diffH < 12) countdown = diffH > 0 ? `em ${diffH}h${diffM > 0 ? ` ${diffM}min` : ""}` : `em ${diffM}min`;
    else if (i === 1) countdown = `amanhã às ${live.start}`;
    else countdown = `${DAY_NAMES[d]} às ${live.start}`;
    return { show: true, isLive: false, countdown };
  }
  return { show: true, isLive: false, countdown: "em breve" };
}

interface HomePageProps {
  overviewCards: OverviewCard[];
  userName?: string;
  featuredCourses: CourseProgressSummary[];
  products: ProductItem[];
  onOpenCourse: (courseId: string) => void;
  accessibleProductsCount: number;
  loadingData: boolean;
  homeBanners?: HomeBanner[];
  liveUrl?: string | null;
  live?: LiveConfig;
  onContinue?: (courseId: string, courseTitle: string, lessonId: number) => void;
  /* props ainda passadas pelo App (não usadas neste layout) */
  isLoggedIn?: boolean;
  loadingAuth?: boolean;
  authError?: string;
  pageError?: string;
  documentInput?: string;
  setDocumentInput?: (value: string) => void;
  onLoginDemo?: () => void;
  onLoginGuest?: () => void;
  onLoginByDocument?: () => void;
}

const isExternal = (href: string) => /^https?:\/\//i.test(href);

function productHref(product: ProductItem) {
  if (product.isCourse) return `#/curso/${product.id}`;
  return product.accessUrl || product.productUrl || product.checkoutUrl || "#/catalogo";
}

// Categoria pago × grátis (rails "Cursos gratuitos"/"Cursos premium").
// Heurística até o backend expor um campo explícito. Ver INTEGRATION.md §1.
const isPaidCourse = (p: ProductItem) => !!p.checkoutUrl || p.isOffer;

function productBadge(product: ProductItem) {
  if (product.isCourse) return "Curso";
  if (/vip|comunidade/i.test(product.title)) return "Comunidade";
  if (/gerenciador|banca|planilha/i.test(product.title)) return "Ferramenta";
  if (product.isOffer) return "Oferta";
  return product.badge || "Produto";
}

function productAction(product: ProductItem) {
  if (product.hasAccess) return product.isCourse ? "Assistir" : "Acessar";
  if (product.requiresExternalSignup) return "Cadastrar";
  if (product.checkoutUrl) return "Desbloquear";
  return "Ver detalhes";
}

const HomePage: React.FC<HomePageProps> = ({
  userName,
  featuredCourses,
  products,
  onOpenCourse,
  loadingData,
  homeBanners = [],
  liveUrl,
  live,
  onContinue,
}) => {
  const courseProducts = products.filter((p) => p.isCourse);
  const freeCourses = courseProducts.filter((p) => !isPaidCourse(p));
  const paidCourses = courseProducts.filter(isPaidCourse);
  const toolProducts = products.filter((p) => !p.isCourse);
  const progressByCourse = new Map(
    featuredCourses.map((course) => [course.courseId, course]),
  );
  const [lastLesson, setLastLesson] = useState<LastLesson | null>(null);
  useEffect(() => {
    try {
      const raw = localStorage.getItem("bc:lastLesson");
      setLastLesson(raw ? (JSON.parse(raw) as LastLesson) : null);
    } catch {
      setLastLesson(null);
    }
  }, []);
  const liveHref = live?.url || liveUrl || "";
  const liveExternal = /^https?:\/\//i.test(liveHref);
  const liveStatus = getLiveStatus(live);
  const lastProgress = lastLesson ? progressByCourse.get(lastLesson.courseId) : null;

  const renderSkeletons = (count: number) =>
    Array.from({ length: count }).map((_, i) => (
      <div className="cc cc-skeleton" key={i} aria-hidden="true">
        <div className="cc-thumb" />
        <div className="cc-body">
          <span className="sk sk-1" />
          <span className="sk sk-2" />
          <span className="sk sk-3" />
        </div>
      </div>
    ));

  const renderCourseCard = (product: ProductItem, index: number) => {
    const progress = progressByCourse.get(product.id);
    const locked = !product.hasAccess;
    const meta = locked
      ? "Bloqueado"
      : progress && progress.totalLessons
        ? `${progress.progressPercent}% · ${progress.watchedLessons}/${progress.totalLessons} aulas`
        : "Pronto para começar";
    return (
      <ContentCard
        key={product.id}
        title={product.title}
        description={product.description}
        cover={product.banner}
        badge={locked ? "Premium" : "Curso"}
        locked={locked}
        progressPercent={!locked && progress ? progress.progressPercent : undefined}
        meta={meta}
        actionLabel={locked ? "Desbloquear" : "Assistir"}
        onClick={locked ? undefined : () => onOpenCourse(product.id)}
        href={locked ? product.checkoutUrl || undefined : undefined}
        external={locked && !!product.checkoutUrl}
        toneIndex={index}
      />
    );
  };

  return (
    <div className="bc-home">
      <Banner banners={homeBanners} liveUrl={liveUrl} userName={userName} />

      {liveStatus.show || lastLesson ? (
      <div className={`bc-home-row ${liveStatus.show && lastLesson ? "is-split" : ""}`}>
      {liveStatus.show && liveStatus.isLive ? (
        <a className="bc-live" href={liveHref || "#"} target={liveExternal ? "_blank" : undefined} rel={liveExternal ? "noreferrer" : undefined}>
          <span className="bc-live-badge"><span className="bc-live-ping" />AO VIVO</span>
          <span className="bc-live-txt">
            <strong>{live?.title}</strong>
            <small>{live?.subtitle}</small>
          </span>
          <span className="bc-live-cta">Entrar na sala <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 6 6 6-6 6" /></svg></span>
        </a>
      ) : liveStatus.show ? (
        <div className="bc-live is-scheduled">
          <span className="bc-live-badge sched">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
            Próxima live
          </span>
          <span className="bc-live-txt">
            <strong>Próxima live {liveStatus.countdown}</strong>
            <small>{live?.offlineText}</small>
          </span>
          <a className="bc-live-cta sched" href="#/utilidades">Acompanhar notícias <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 6 6 6-6 6" /></svg></a>
        </div>
      ) : null}

      {lastLesson ? (
        <button type="button" className="bc-continue" onClick={() => onContinue && onContinue(lastLesson.courseId, lastLesson.courseTitle, lastLesson.lessonId)}>
          <span className="bc-continue-thumb" style={lastLesson.cover ? { backgroundImage: `url("${lastLesson.cover}")` } : undefined}>
            <span className="bc-continue-play"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7L8 5Z" /></svg></span>
          </span>
          <span className="bc-continue-body">
            <span className="bc-continue-eyebrow"><i />Continue assistindo</span>
            <strong>{lastLesson.lessonTitle}</strong>
            <small>{lastLesson.courseTitle} · {lastLesson.moduleTitle}</small>
            {lastProgress ? (
              <span className="bc-continue-prog"><i style={{ width: `${lastProgress.progressPercent}%` }} /></span>
            ) : null}
          </span>
          <span className="bc-continue-cta">Continuar <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7L8 5Z" /></svg></span>
        </button>
      ) : null}
      </div>
      ) : null}

      {loadingData ? (
        <section className="bc-sec">
          <div className="bc-sec-head"><h2>Seus cursos</h2></div>
          <div className="bc-rail">{renderSkeletons(3)}</div>
        </section>
      ) : !courseProducts.length ? (
        <section className="bc-sec">
          <div className="bc-sec-head">
            <h2>Seus cursos</h2>
            <a className="bc-sec-more" href="#/curso">Ver todos →</a>
          </div>
          <div className="bc-rail"><p className="bc-empty">Nenhum curso disponível ainda.</p></div>
        </section>
      ) : (
        <>
          {freeCourses.length ? (
            <section className="bc-sec">
              <div className="bc-sec-head">
                <h2>Cursos gratuitos</h2>
                <a className="bc-sec-more" href="#/curso">Ver todos →</a>
              </div>
              <div className="bc-rail">{freeCourses.map(renderCourseCard)}</div>
            </section>
          ) : null}
          {paidCourses.length ? (
            <section className="bc-sec">
              <div className="bc-sec-head">
                <h2>Cursos premium</h2>
                <a className="bc-sec-more" href="#/curso">Ver todos →</a>
              </div>
              <div className="bc-rail">{paidCourses.map(renderCourseCard)}</div>
            </section>
          ) : null}
        </>
      )}

      <section className="bc-sec">
        <div className="bc-sec-head">
          <h2>Ferramentas e acessos</h2>
          <a className="bc-sec-more" href="#/catalogo">Ver todas →</a>
        </div>
        <div className="bc-rail">
          {loadingData
            ? renderSkeletons(4)
            : toolProducts.length
              ? toolProducts.map((product, index) => {
                  const href = productHref(product);
                  return (
                    <ContentCard
                      key={product.id}
                      title={product.title}
                      description={product.description}
                      cover={product.banner}
                      badge={productBadge(product)}
                      locked={!product.hasAccess}
                      meta={product.hasAccess ? "Liberado" : "Bloqueado"}
                      actionLabel={productAction(product)}
                      href={href}
                      external={isExternal(href)}
                      toneIndex={index + 2}
                    />
                  );
                })
              : <p className="bc-empty">Nenhuma ferramenta disponível ainda.</p>}
        </div>
      </section>
    </div>
  );
};

export default HomePage;
